// Global scene assistants: preset prompt-writing agents that give a node its
// identity (accent glow + label). Source of truth is shared with the backend:
//   front2/agent-manual/Scene_Assistants/catalog.json
import catalog from '../../agent-manual/Scene_Assistants/catalog.json'

export const DEFAULT_SCENE_ASSISTANT_ACCENT = '#ffffff'

const HEX_COLOR_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i

const cleanString = (value) => (typeof value === 'string' ? value.trim() : '')

const positiveNumber = (value, fallback) => {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : fallback
}

const normalizeDuration = (raw) => {
  const min = positiveNumber(raw?.min, 4)
  const max = Math.max(min, positiveNumber(raw?.max, min))
  const preferred = positiveNumber(raw?.default, min)
  return { min, max, default: Math.min(max, Math.max(min, preferred)) }
}

const normalizeImageSlots = (raw) => {
  if (!Array.isArray(raw)) return []
  const seen = new Set()
  return raw
    .map((slot) => ({
      key: cleanString(slot?.key),
      label: cleanString(slot?.label) || cleanString(slot?.key),
      required: slot?.required === true,
    }))
    .filter((slot) => {
      if (!slot.key || seen.has(slot.key)) return false
      seen.add(slot.key)
      return true
    })
}

export function normalizeSceneAssistant(raw) {
  const id = cleanString(raw?.id)
  if (!id) return null
  const accentColor = cleanString(raw.accentColor)
  return Object.freeze({
    id,
    label: cleanString(raw.label) || id,
    introMessage: cleanString(raw.introMessage),
    routingDescription: cleanString(raw.routingDescription),
    accentColor: HEX_COLOR_RE.test(accentColor)
      ? accentColor
      : DEFAULT_SCENE_ASSISTANT_ACCENT,
    outputKind: raw.outputKind === 'image' ? 'image' : 'video',
    recommendedFlowId: cleanString(raw.recommendedFlowId) || null,
    durationSec: Object.freeze(normalizeDuration(raw.durationSec)),
    imageSlots: Object.freeze(normalizeImageSlots(raw.imageSlots)),
    instructionsFile: cleanString(raw.instructionsFile),
  })
}

export const SCENE_ASSISTANTS = Object.freeze(
  (Array.isArray(catalog?.assistants) ? catalog.assistants : [])
    .map(normalizeSceneAssistant)
    .filter(Boolean),
)

const SCENE_ASSISTANTS_BY_ID = new Map(SCENE_ASSISTANTS.map((a) => [a.id, a]))

export function getSceneAssistant(id) {
  return SCENE_ASSISTANTS_BY_ID.get(cleanString(id)) || null
}

// Posted in the prompt bar right after the assistant's node is spawned.
export function sceneAssistantIntro(assistant) {
  if (!assistant) return ''
  const template =
    assistant.introMessage ||
    'I have added a {label} node below. Tell me roughly what you want and I will write its prompt.'
  return template.replace(/\{label\}/g, assistant.label)
}

// payload: { assistantId, message, currentPrompt, durationSec,
//            images: [{ slotKey, fileId }], history: [{ role, content }] }
export async function requestSceneAssistant({
  socket,
  timeoutMs = 120000,
  ...payload
} = {}) {
  if (!socket || typeof socket.emitWithAck !== 'function') {
    return { ok: false, error: 'Scene assistant is not connected.' }
  }
  try {
    const reply = await socket
      .timeout(Math.max(1000, Number(timeoutMs) || 120000))
      .emitWithAck('vyby:scene_assistant', payload)
    const text = `${reply?.text || ''}`.trim()
    if (!reply?.success || !text) {
      return {
        ok: false,
        error: reply?.message || reply?.error || 'Scene assistant failed.',
      }
    }
    return { ok: true, text }
  } catch (error) {
    return {
      ok: false,
      error: error?.message || 'Scene assistant request timed out.',
    }
  }
}

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// The assistant writes characters as @Name. Turn those back into element
// tokens for elements the user owns so their reference images attach at
// generation time. Longest names first so "@Kai Chen" wins over "@Kai".
export function elementTokensFromAssistantText(text, elements) {
  let out = `${text || ''}`
  const named = (Array.isArray(elements) ? elements : [])
    .filter(
      (el) =>
        el?.id &&
        cleanString(el.name) &&
        el.kind !== 'personality' &&
        el.kind !== 'mood',
    )
    .sort((a, b) => cleanString(b.name).length - cleanString(a.name).length)
  named.forEach((el) => {
    const re = new RegExp(`@${escapeRegExp(cleanString(el.name))}(?![\\w])`, 'gi')
    out = out.replace(re, `{{element:${el.id}}}`)
  })
  return out
}

// ---- Per-node scene assistant state -------------------------------------
// node.sceneAssistantId  string|null   catalog id; unknown ids are kept so a
//                                      catalog rollback doesn't wipe nodes
// node.assistantImages   { [slotKey]: fileId }  assistant-only references,
//                                      separate from the generation inputs
// node.assistantThread   [{ id, role, content, at }]  role: user | assistant
//                                      | intro | enhance (intro and enhance
//                                      entries are display history only, never
//                                      sent as conversation)

export const MAX_ASSISTANT_THREAD_ENTRIES = 50
const ASSISTANT_THREAD_ROLES = new Set(['user', 'assistant', 'intro', 'enhance'])

export function normalizeSceneAssistantId(raw) {
  return cleanString(raw) || null
}

export function normalizeAssistantImages(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return {}
  const out = {}
  Object.keys(raw).forEach((slotKey) => {
    const key = cleanString(slotKey)
    const fileId = cleanString(raw[slotKey])
    if (key && fileId) out[key] = fileId
  })
  return out
}

export function normalizeAssistantThread(raw) {
  if (!Array.isArray(raw)) return []
  return raw
    .map((entry, index) => {
      const role = cleanString(entry?.role)
      const content = typeof entry?.content === 'string' ? entry.content : ''
      if (!ASSISTANT_THREAD_ROLES.has(role) || !content.trim()) return null
      const at = Number(entry.at)
      return {
        id: cleanString(entry.id) || `t-${index}-${Math.random().toString(16).slice(2)}`,
        role,
        content,
        at: Number.isFinite(at) && at > 0 ? at : 0,
      }
    })
    .filter(Boolean)
    .slice(-MAX_ASSISTANT_THREAD_ENTRIES)
}

// Keeps only images for slots the next assistant declares.
export function assistantImagesForAssistant(images, assistantId) {
  const assistant = getSceneAssistant(assistantId)
  if (!assistant) return {}
  const allowed = new Set(assistant.imageSlots.map((slot) => slot.key))
  const current = normalizeAssistantImages(images)
  const out = {}
  Object.keys(current).forEach((key) => {
    if (allowed.has(key)) out[key] = current[key]
  })
  return out
}

// Asks the backend JEV router which agent / scene assistant fits a prompt.
// surface: 'canvas' (agent + scene assistant) | 'editor' (agent only).
// `previous` ({ request, scene }) lets the router flag follow-ups.
export async function requestAgentRoute({
  socket,
  message,
  surface = 'canvas',
  previous = null,
  timeoutMs = 15000,
} = {}) {
  const text = `${message || ''}`.trim()
  if (!text) return { ok: false, error: 'Router needs a message.' }
  if (!socket || typeof socket.emitWithAck !== 'function') {
    return { ok: false, error: 'Router is not connected.' }
  }
  try {
    const reply = await socket
      .timeout(Math.max(1000, Number(timeoutMs) || 15000))
      .emitWithAck('vyby:route', {
        message: text,
        surface,
        ...(previous ? { previous } : {}),
      })
    if (!reply?.success || !reply.route) {
      return {
        ok: false,
        error: reply?.message || reply?.error || 'Router failed.',
      }
    }
    return { ok: true, route: reply.route }
  } catch (error) {
    return { ok: false, error: error?.message || 'Router request failed.' }
  }
}
